// @givmo/passkeys — where a passkey can work at all: Givmo's relying party, and this page's host.
//
// A browser runs a WebAuthn ceremony only on a page whose host is the relying party's id or a
// subdomain of it, and refuses any other before the backend is asked. Givmo's RP id is `givmo.io`
// (Jonathan's D-AS-10, 2026-07-27): one host in every environment, the one whose association files
// the apps read. It is a constant, never config: a passkey is bound for good to the RP id it was made
// under, so a wrong one orphans every passkey made with it, and no deploy may vary it.
//
// So a console served from another host (a Firebase `*.web.app` site) can run no ceremony against
// any deployed backend, and draws no passkey surface there. The surfaces that ask
// `passkeysUsableHere()` beside the backend's own read: the Sign-in & security card and the account
// menu's row to it on all three consoles, and on the company console its sign-in door's passkey
// button and its "Confirm it's you" sheet's passkey arm too (EP-MNT18), and on the company and charity
// consoles the first-passkey offer their hosts hand `@givmo/org-console`'s acts (MCP-P6d). The employer
// portal's and the charity console's doors and sheet arms, and Compliance's setup offer, do not ask
// it. On `localhost`
// a ceremony runs only against a local backend served with `PASSKEYS_RP_ID=localhost`, and these
// surfaces draw nothing there: a local passkey review serves the console under a `givmo.io` name
// (`agent_docs/auth.md` § Running a passkey change against dev before it deploys).

/** Givmo's passkey relying party id (D-AS-10): one host in every environment, never config. */
export const PASSKEY_RP_ID = 'givmo.io';

/**
 * Whether a page on `hostname` can run a passkey ceremony: the host is {@link PASSKEY_RP_ID} or a
 * subdomain of it. Defaults to this page's host; `false` where there is no page (a server render).
 */
export function passkeysUsableHere(hostname: string = pageHost()): boolean {
  const host = hostname.toLowerCase();
  return host === PASSKEY_RP_ID || host.endsWith(`.${PASSKEY_RP_ID}`);
}

function pageHost(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname;
}
